import Papa from 'papaparse';

import { BulkAssetRow } from '../types';

interface ParseBulkAssetCsvOptions {
  includeRowsMissingVin?: boolean;
}

// Column headers reduced to letters/digits only, so matching is tolerant of
// the template's casing, spaces, the required-field `*`, `#`, `/`, and any BOM
// (e.g. "Account*", "FMS ID*", "Unit #*", "VIN*").
const COLUMN_HEADERS = {
  account: 'account',
  depotFmsId: 'fmsid',
  vin: 'vin',
  unitNumber: 'unit',
  odometer: 'odometer',
  engineHours: 'enginehours',
  fleetInfo: 'fleetinfo',
};

const normalizeHeader = (header: string): string =>
  header.toLowerCase().replace(/[^a-z0-9]/g, '');

const cell = (
  row: Record<string, unknown>,
  normalizedHeader: string,
): string => {
  const key = Object.keys(row).find(
    (k) => normalizeHeader(k) === normalizedHeader,
  );
  return key ? String(row[key] ?? '').trim() : '';
};

// Coerce an optional integer cell (e.g. Odometer "150,987") to a number,
// stripping thousands separators. Empty/non-numeric cells become undefined.
const toInteger = (value: string): number | undefined => {
  const normalized = value.trim().replace(/[,\s]/g, '');
  if (!normalized || !/^\d+$/.test(normalized)) return undefined;
  return Number(normalized);
};

export const parseBulkAssetCsv = (
  file: File,
  options: ParseBulkAssetCsvOptions = {},
): Promise<BulkAssetRow[]> =>
  new Promise((resolve, reject) => {
    const { includeRowsMissingVin = false } = options;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = (result.data as Record<string, unknown>[]).reduce<
          BulkAssetRow[]
        >((acc, raw) => {
          const account = cell(raw, COLUMN_HEADERS.account);
          const depotFmsId = cell(raw, COLUMN_HEADERS.depotFmsId);
          const vin = cell(raw, COLUMN_HEADERS.vin);
          const unitNumber = cell(raw, COLUMN_HEADERS.unitNumber);
          const hasRequiredBaseFields = account && depotFmsId && unitNumber;

          if (hasRequiredBaseFields && (vin || includeRowsMissingVin)) {
            acc.push({
              account,
              depotFmsId,
              vin,
              unitNumber,
              mileage: toInteger(cell(raw, COLUMN_HEADERS.odometer)),
              engineHours: toInteger(cell(raw, COLUMN_HEADERS.engineHours)),
              fleetInfo: cell(raw, COLUMN_HEADERS.fleetInfo) || undefined,
            });
          }

          return acc;
        }, []);

        resolve(rows);
      },
      error: (error) =>
        reject(new Error(`Failed to parse CSV: ${error.message}`)),
    });
  });
